import { useMemo } from "react";
import moment from "moment";
import { isNumber } from "lodash";

import { QueryStatus, useQuery } from "react-query";
import { QUERY_KEY_STRINGS } from "../../shared-types";
import { useAppUrl } from "../../helpers/use-app-url";

import { FeatureCollection } from "geojson";
import {
  TropicalStorm,
  TropicalStormClassification,
  TropicalStormConeOfUncertainty,
  TropicalStormPosition,
  TropicalStormsContextType,
  TropicalStormWindDangerSwath,
  WindQuadrant,
  WindQuadrantValue,
} from ".";

import config from "../../config";
import {
  normalizeLongitude,
  removeAntimeridianDiscontinuityFromGeometry,
} from "../../helpers/geometry";

export type StormGlassResponse = {
  requestTime: string;
  createdAt: string;
  forecasts: StormGlassStorm[];
  dataVersion: string;
};

export type StormGlassStorm = {
  attributes: {
    basin_id: string;
    classification: string;
    publish_date_time: string;
    publishing_center: string;
    storm_name: string;
    warning_number: number;
  };
  geojson: FeatureCollection;
};

type StormGlassWindQuadrant = {
  description: string;
  direction: "NE" | "SE" | "SW" | "NW";
  radius_nm: number;
};

const parseWindQuadrant = (
  quadrant?: StormGlassWindQuadrant
): WindQuadrantValue | undefined => {
  if (!quadrant) return undefined;
  return {
    description: quadrant.description,
    radiusNauticalMiles: quadrant.radius_nm,
  };
};

export const parseWindSpeedToClassification = (
  windSpeedKnots: number | undefined,
  storm?: StormGlassStorm
): TropicalStormClassification => {
  if (isNumber(windSpeedKnots)) {
    // Based on https://www.nhc.noaa.gov/climo/?text
    // Specialized by https://app.clubhouse.io/sofar/story/15275/draw-storm-track-with-icons-on-frontend#activity-18948
    if (windSpeedKnots <= 33) return "Depression";
    if (windSpeedKnots >= 34 && windSpeedKnots <= 47) return "Tropical Storm";
    if (windSpeedKnots >= 48 && windSpeedKnots <= 63)
      return "Severe Tropical Storm";
    if (windSpeedKnots >= 64 && windSpeedKnots <= 95) return "Hurricane";
    if (windSpeedKnots >= 96) return "Severe Hurricane";
  }
  console.error(
    `Could not parse wind speed '${windSpeedKnots}' for storm '${storm?.attributes.storm_name}' ` +
      `from center '${storm?.attributes.publishing_center}'.`
  );
  return "Depression";
};

const parseWindQuadrants = (
  stormGlassQuadrants: any,
  storm: StormGlassStorm
): WindQuadrant[] => {
  if (!stormGlassQuadrants) return [];

  return Object.keys(stormGlassQuadrants).map((key) => {
    const windSpeed = Number(key);
    const quadrants = stormGlassQuadrants[key] as StormGlassWindQuadrant[];

    return {
      windSpeedKnots: windSpeed,
      NE: parseWindQuadrant(quadrants.find((q) => q.direction === "NE")),
      NW: parseWindQuadrant(quadrants.find((q) => q.direction === "NW")),
      SE: parseWindQuadrant(quadrants.find((q) => q.direction === "SE")),
      SW: parseWindQuadrant(quadrants.find((q) => q.direction === "SW")),
    } as WindQuadrant;
  });
};

export const zeroPadTwo = (num: number) => String(num).padStart(2, "0");

export const parseTropicalStormDateTime = (
  date: string,
  createdAtTimestamp: string
) => {
  const baseDate = moment(createdAtTimestamp);
  const datePart = baseDate.toISOString().split("T").shift();

  let year = datePart?.substring(0, 4);
  let month = datePart?.substring(5, 7);
  const day = date.substring(0, 2);
  const time = date.substring(2);

  if (Number(day) < baseDate.day()) {
    month = zeroPadTwo(Number(month) + 1);
    if (Number(month) === 13) {
      month = "01";
      year = `${Number(year) + 1}`;
    }
  }

  return `${year}-${month}-${day}T${time.substring(0, 2)}:${time.substring(
    2,
    4
  )}:00.000Z`;
};

export const parseTropicalStorms = (
  stormData: StormGlassStorm[]
): TropicalStorm[] => {
  if (!stormData?.length) return [];

  return (
    stormData
      // We can have the same storm from NHC and from JTWC.
      // If that is the case, we prefer to use the JTWC for now
      // until we have clarity that NHC works.
      .filter((s) => {
        if (
          // if we have more than one storm with the same name
          stormData
            .map((sc) => sc.attributes.storm_name)
            .filter((sc) => sc === s.attributes.storm_name).length > 1
        ) {
          // make sure the publishing_center is not KNHC
          // (JTWC has multiple publishing centers)
          return s.attributes.publishing_center !== "KNHC";
        }
        return true;
      })
      .map((storm) => {
        return {
          basinID: storm.attributes?.basin_id,
          classification: storm.attributes?.classification,
          publishingCenter: storm.attributes?.publishing_center,
          name: storm.attributes?.storm_name,
          warningNumber: storm.attributes?.warning_number,
          positions: storm.geojson.features.length
            ? storm.geojson.features
                // Point features are used for storm positions
                .filter((position) => position.geometry.type === "Point")
                .map((position) => {
                  return {
                    forecastDateTime: position.properties?.forecast_datetime
                      ? new Date(
                          position.properties?.forecast_datetime
                        ).toISOString()
                      : undefined,
                    forecastDayHour: position.properties?.forecast_day_hour,
                    forecastHour: position.properties?.forecast_hour,
                    gustSpeedKnots: position.properties?.gusts_speed_kts,
                    maxSignificantWaveHeightMeters:
                      position.properties?.max_sig_wave_ht_m,
                    movementDeg:
                      position.properties?.past_movement_deg ||
                      position.properties?.movement_deg,
                    movementKnots:
                      position.properties?.past_movement_kts ||
                      position.properties?.movement_kts,
                    nextForecastHour: position.properties?.next_forecast_hour,
                    windSpeedKnots: position.properties?.wind_speed_kts,

                    position: {
                      lon: (position.geometry as GeoJSON.Point).coordinates[0],
                      lat: (position.geometry as GeoJSON.Point).coordinates[1],
                    },

                    windQuadrants: parseWindQuadrants(
                      position.properties?.wind_radii,
                      storm
                    ),

                    classification: parseWindSpeedToClassification(
                      position.properties?.wind_speed_kts,
                      storm
                    ),
                  } as TropicalStormPosition;
                })
            : [],
          windDangerSwath: storm.geojson.features.length
            ? storm.geojson.features
                // Polygon features are used for wind warning swaths
                .filter((position) => position.geometry.type === "Polygon")
                .map((position) => {
                  if (position.id === "34 knot Danger Swath") {
                    return position as TropicalStormWindDangerSwath;
                  } else {
                    return undefined;
                  }
                })[0]
            : undefined,
          coneOfUncertainty: storm.geojson.features.length
            ? storm.geojson.features
                .filter((position) => position.geometry.type === "Polygon")
                .map((position) => {
                  if (position.id === "Cone of Uncertainty") {
                    return position as TropicalStormConeOfUncertainty;
                  } else {
                    return undefined;
                  }
                })[0]
            : undefined,
        } as TropicalStorm;
      })
  );
};

const useTropicalStormsState = (): TropicalStormsContextType => {
  // Allow us to set a time in the query parameters to retrieve a historical
  // forecast
  const { simulatedHistoricalTime } = useAppUrl();
  let simulatedHistoricalTimeString =
    simulatedHistoricalTime?.toISOString() ?? "";

  const { status, data: tropicalStormData } = useQuery(
    // `react-query` will automatically de-dupe and cache results for all
    // requests that share this query key
    QUERY_KEY_STRINGS.TROPICAL_STORMS,
    () => fetchLatestTropicalStorms(simulatedHistoricalTimeString),
    {
      // Automatically re-fetch Tropical storm data according to this interval.
      // `react-query` automatically handles the start/stop poll behavior for
      // us, so this will only poll when at least one component consuming
      // this query is mounted, and it will poll at most once regardless of
      // how many components consuming this query are mounted.
      refetchInterval: config.tropicalStormsPollIntervalMs,

      // Don't re-fetch data ever unless it is older than this amount of time.
      // `staleTime` defaults to 0, which means cached data will be used in
      // subsequent requests but, in parallel, new data will be fetched as
      // well.
      staleTime: config.tropicalStormsPollIntervalMs - 1,
    }
  );

  const storms = useMemo(() => {
    const storms = tropicalStormData || [];
    storms.forEach((s) => {
      // remove antimeridian discontinuity from coords
      if (s.positions?.[0])
        s.positions.forEach((p) => {
          p.position.lon =
            normalizeLongitude(p.position.lon - s.positions[0].position.lon) +
            s.positions[0].position.lon;
        });
      if (s.windDangerSwath) {
        removeAntimeridianDiscontinuityFromGeometry(s.windDangerSwath.geometry);
      }
      if (s.coneOfUncertainty) {
        removeAntimeridianDiscontinuityFromGeometry(
          s.coneOfUncertainty.geometry
        );
      }
    });

    return storms;
  }, [tropicalStormData]);

  return useMemo(
    () => ({
      isLoading: status === QueryStatus.Loading,
      isLoaded: status === QueryStatus.Success,
      storms: storms,
    }),
    [status, storms]
  );
};

const fetchLatestTropicalStorms = async (
  simulatedHistoricalTimeString: string
) => {
  const latestData = (await (
    await fetch(
      `${config.stormGlassTropicalStormForeCastAPI}${
        simulatedHistoricalTimeString.length > 0
          ? `?availableAt=${simulatedHistoricalTimeString}`
          : ""
      }`
    )
  ).json()) as StormGlassResponse;

  return Promise.resolve(
    latestData.forecasts?.length
      ? parseTropicalStorms(latestData.forecasts)
      : []
  );
};

export default useTropicalStormsState;
